import React, { useEffect, useState } from 'react';
import { TranslationUtils, ValidationUtils } from '../../utils';
import { useDebounce } from '../../hooks';
import { useSelector } from 'react-redux';
import { Button, Loading, ManageGuestCount } from '../../components';
import * as _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { TripService } from '../../services/trip/tripService';
import ddLogger from '../../logging/ddLogger';
import { AppRoutes } from '../../routes';
import './ModifyGuests.scss';
import { OccupancyCost, OccupancyCounts, OccupancyKeys } from '../../types/occupancy';
import { ManageGuestConfirmationProps } from '../manage-guest-confirmation/ManageGuestConfirmation';
import { AlertMessage, Icon } from '@vacasa/react-components-lib';
import { StoreState } from '../../types/store.type';
import { TripState } from '../../types/trip.store.type';
import segmentService, { Step } from '../../services/segment/segmentService';
import { AdjustOccupancyAndPetsPreviewRequest, AdjustOccupancyPreviewRequest, TripAdjustmentResponse } from 'guest-data-types-ts-lib/types';
import { AdjustmentConfig, Splits } from '../../types/split.type';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

export const ModifyGuests: React.FC = () => {
    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const [initialOccupancy, setInitialOccupancy] = useState<OccupancyCounts | null>(null);
    const [occupancyCounts, setOccupancyCounts] = useState<OccupancyCounts | null>(null);
    const [renderedCounts, setRenderedCounts] = useState<OccupancyCounts | null>(null);
    const [occupancyCost, setOccupancyCost] = useState<OccupancyCost | undefined>(undefined);
    const [isFetchingPreview, setIsFetchingPreview] = useState<boolean>(false);
    const [adjustmentPreview, setAdjustmentPreview] = useState<TripAdjustmentResponse | null>(null);
    const [isWaitingForDebounceCallback, setIsWaitingForDebounceCallback] = useState<boolean>(false);

    // Feature Flag
    const {
        isEnabled: isManagePetsEnable,
        config: managePetsConfig,
        clientReady: isManagePetsReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_PET_ADJUSTMENT, false);

    const {
        isEnabled: isNewOccupancyEnable,
        config: newOccupancyConfig,
        clientReady: isNewOccupancyReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_OCCUPANCY_ADJUSTMENT, false);

    const [error, setError] = useState<Error | null>(null);
    const debounceDelay = 1000;
    const { language } = useSelector((store: StoreState) => store.user);
    const history = useHistory();
    const { home_info, reservation, guests, required_actions, unit } = trip;

    let phoneNumber = ValidationUtils.validateAndGetEnvVar('REACT_APP_TOLL_FREE_PHONE_NUMBER');
    const isNewAddPetAvailable = isFeatureAvailableForChannel(reservation!, isManagePetsReady, managePetsConfig, isManagePetsEnable);

    const adultsSlug = TranslationUtils.get('payment_form_adults');
    const childrenSlug = TranslationUtils.get('payment_form_children');
    const petsSlug = TranslationUtils.get('pets');

    useEffect(() => {
        if (home_info && guests) {
            const { adults, children, pets } = guests.current_counts;

            const initialOccupancy = {
                [OccupancyKeys.ADULTS]: { name: adultsSlug, count: adults, min: 1, max: guests.max_counts.adults },
                [OccupancyKeys.CHILDREN]: { name: childrenSlug, count: children, max: guests.max_counts.children },
            } as OccupancyCounts;

            if (home_info.allows_dogs && !isNewAddPetAvailable) {
                initialOccupancy[OccupancyKeys.PETS] = { name: petsSlug, count: pets, max: guests.max_counts.pets };
            }

            setInitialOccupancy(initialOccupancy);
            setRenderedCounts(initialOccupancy);
        }
    }, [home_info, guests]);

    useEffect(() => {
        setRenderedCounts((current) => {
            if (current) {
                current[OccupancyKeys.ADULTS] = { ...current[OccupancyKeys.ADULTS], name: adultsSlug };
                current[OccupancyKeys.CHILDREN] = { ...current[OccupancyKeys.CHILDREN], name: childrenSlug };

                if (OccupancyKeys.PETS in current) {
                    current[OccupancyKeys.PETS] = { ...current[OccupancyKeys.PETS], name: petsSlug };
                }

                return current;
            }
            return null;
        });
    }, [language]);

    const getAdjustmentPreview = async (
        reservationId: string,
        request: AdjustOccupancyAndPetsPreviewRequest | AdjustOccupancyPreviewRequest
    ): Promise<TripAdjustmentResponse | undefined> => {
        try {
            setError(null);
            setIsFetchingPreview(true);
            const { attributes: preview } = (await new TripService().getAdjustmentPreview(reservationId, request)) as {
                attributes: TripAdjustmentResponse;
            };
            segmentService.trackAdjustmentSuccess(Step.PREVIEW, preview, {});
            return preview;
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR previewing adjustOccupancyAndPets adjustment for ${reservationId}`, {
                adjustment: 'adjustOccupancyAndPets',
                error: e.message,
                code,
            });
            segmentService.trackAdjustmentIssue(request, code);
            setError(e);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    useDebounce(
        async () => {
            if (!occupancyCounts || !reservation) {
                return;
            }
            const data: { [key: string]: number } = {
                [OccupancyKeys.ADULTS]: occupancyCounts[OccupancyKeys.ADULTS].count,
                [OccupancyKeys.CHILDREN]: occupancyCounts[OccupancyKeys.CHILDREN].count,
            };

            if (OccupancyKeys.PETS in occupancyCounts) {
                data[OccupancyKeys.PETS] = occupancyCounts[OccupancyKeys.PETS].count;
            }

            const isNewOccupancyFlowAvailable = isFeatureAvailableForChannel(
                reservation,
                isNewOccupancyReady,
                newOccupancyConfig,
                isNewOccupancyEnable
            );
            const occupancyAdjustmentName = isNewOccupancyFlowAvailable ? 'adjustOccupancy' : 'adjustOccupancyAndPets';

            const request: AdjustOccupancyAndPetsPreviewRequest | AdjustOccupancyPreviewRequest = {
                name: occupancyAdjustmentName,
                data,
            };

            const preview = await getAdjustmentPreview(reservation.id, request);

            if (!preview) {
                return;
            }

            setAdjustmentPreview(preview);

            const { adults, children, pets, detail } = preview;

            const newCounts = {
                [OccupancyKeys.ADULTS]: { ...occupancyCounts[OccupancyKeys.ADULTS], count: adults },
                [OccupancyKeys.CHILDREN]: { ...occupancyCounts[OccupancyKeys.CHILDREN], count: children },
            } as OccupancyCounts;

            if (OccupancyKeys.PETS in occupancyCounts) {
                newCounts[OccupancyKeys.PETS] = { ...occupancyCounts[OccupancyKeys.PETS], count: pets };
            }

            const newCost: OccupancyCost = {
                total: { total: detail!.total, currency: detail!.currency },
                adjustment: { total: detail!.amount, currency: detail!.currency },
                convertedTotal: detail!.converted
                    ? {
                          total: detail!.converted.total,
                          currency: detail!.converted.currency,
                      }
                    : undefined,
            };

            if (balance > 0) {
                newCost.previousBalance = { total: balance, currency: currency_code! };
            }

            setRenderedCounts(newCounts);
            setOccupancyCost(newCost);

            setIsWaitingForDebounceCallback(false);
        },
        debounceDelay,
        [occupancyCounts]
    );

    if (trip.isLoading) {
        return <Loading />;
    }

    if (!home_info || !reservation || !guests || !required_actions) {
        return null;
    }

    if (unit) {
        phoneNumber = unit.local_phone_number;
    }

    const { allows_dogs, max_occupancy } = home_info;
    const { balance } = reservation;
    const { pay_balance } = required_actions;
    const { currency_code } = pay_balance || {};

    const allowsPets = allows_dogs || false;
    const hasRefundInProgress = balance < 0;

    const handleGuestCountChange = async (updatedCounts: OccupancyCounts) => {
        setRenderedCounts(updatedCounts);
        setOccupancyCounts(updatedCounts);
        setIsWaitingForDebounceCallback(true);
    };

    const handleGuestCountReset = () => {
        setRenderedCounts(initialOccupancy);
        setOccupancyCounts(initialOccupancy);
        setOccupancyCost(undefined);
    };

    const handleConfirm = () => {
        const adjustmentConfirmationProps: ManageGuestConfirmationProps = {
            initialOccupancy,
            occupancyCounts,
            preview: adjustmentPreview,
        };
        history.push({
            pathname: AppRoutes.MODIFY_CONFIRMATION,
            state: adjustmentConfirmationProps,
        });
    };

    const maxCounts = { max: max_occupancy, rules: [OccupancyKeys.ADULTS, OccupancyKeys.CHILDREN] };

    const isDifferentThanOriginal = occupancyCounts && !_.isEqual(initialOccupancy, occupancyCounts);
    const isConfirmEnabled = isDifferentThanOriginal && !isWaitingForDebounceCallback;

    return (
        <div>
            <h1 tabIndex={0} className="heading-large page-title">
                {TranslationUtils.get('manage_guests_title')}
            </h1>
            {hasRefundInProgress && <AlertMessage type="warning" text={TranslationUtils.get('trip_eligibility_refund_in_progress')} />}
            {!hasRefundInProgress && renderedCounts && (
                <>
                    {allowsPets ? (
                        <p className={'body-small'}>{TranslationUtils.get('instructions_for_modify_guests_pets')}</p>
                    ) : (
                        <p className={'body-small '}>{TranslationUtils.get('instructions_for_modify_guests')}</p>
                    )}

                    {renderedCounts && (
                        <ManageGuestCount
                            counts={renderedCounts}
                            maxCount={maxCounts}
                            onChange={handleGuestCountChange}
                            onReset={handleGuestCountReset}
                            isLoading={isFetchingPreview}
                            isResetDisabled={isFetchingPreview || isWaitingForDebounceCallback}
                            isError={!!error}
                            cost={occupancyCost}
                        />
                    )}
                    {error && (
                        <div className="occupancy-adjustment-error">
                            <Icon.AlertTriangle />
                            <p className={'body-small'}>
                                <span>{TranslationUtils.get('an_error_occurred')}</span>.{' '}
                                <span>{TranslationUtils.get('try_later_or_call')}</span>: <span>{phoneNumber}</span>
                            </p>
                        </div>
                    )}
                    <Button
                        id="btn-modify-guests-confirm"
                        className="body-regular btn-modify-guests-confirm mt-3"
                        variant="primary"
                        type="submit"
                        label="Next"
                        disabled={!isConfirmEnabled}
                        isLoading={isFetchingPreview}
                        onClick={handleConfirm}
                    />
                </>
            )}
        </div>
    );
};
